import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import { submitLeadWithWhatsApp } from "../lib/whatsappHandoff";
import { toast } from "./ui/sonner";

/**
 * MiniLeadForm
 *
 * A single reusable "get in touch" form used in two places:
 *   1. Inside <QcAuditPopup /> (Section 7, first-visit modal).
 *   2. On /privacy, /terms, and the 404 page as a compact always-on band
 *      near the footer (Section 8).
 *
 * Fields collected: Name, Company name, Email, "Are you live on Quick
 * Commerce, yes or no". Submitting the form:
 *   1. Opens WhatsApp SYNCHRONOUSLY with the details pre-filled (primary
 *      action), so the tab is never pop-blocked.
 *   2. POSTs the record to `/api/leads` with the caller-supplied
 *      `lead_type` and `source` tags (silent, fire-and-forget).
 *
 * Props:
 *   source          Per-page tag (`qc-audit-popup`, `privacy-mini`, ...).
 *   leadType        Default `"qc-audit"`.
 *   headline        Optional H3 shown above the form.
 *   subhead         Optional supporting copy under the headline.
 *   variant         `"popup"` (stacked) or `"band"` (single-row on desktop).
 *   pagePath        Explicit page path to include in the WhatsApp body.
 *   onDone          Optional callback fired after the WhatsApp draft opens.
 */
export const MiniLeadForm = ({
  source,
  leadType = "qc-audit",
  headline = null,
  subhead = null,
  variant = "popup",
  pagePath,
  onDone,
}) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    liveOnQc: "",
  });

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLive = (value) => setForm((prev) => ({ ...prev, liveOnQc: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.liveOnQc) {
      toast.error("Please fill Name, Company, Email, and the Quick Commerce question.");
      return;
    }

    submitLeadWithWhatsApp({
      payload: {
        name: form.name,
        brand: form.company,
        email: form.email,
        product_category: "QC Audit",
        context: `Live on Quick Commerce: ${form.liveOnQc}`,
        lead_type: leadType,
        source,
      },
      whatsapp: {
        title: "New PickQuick QC audit request",
        source,
        pagePath,
        fields: {
          Name: form.name,
          Company: form.company,
          Email: form.email,
          "Live on Quick Commerce": form.liveOnQc,
        },
      },
    });

    toast.success("Opening WhatsApp with your details, we will pick up from there.");
    setForm({ name: "", company: "", email: "", liveOnQc: "" });
    if (onDone) onDone();
  };

  const inputStyle = {
    width: "100%",
    height: "44px",
    padding: "0 14px",
    backgroundColor: "#0F0F0F",
    border: "1px solid #262626",
    borderRadius: "8px",
    color: "#FFFFFF",
    fontSize: "15px",
    outline: "none",
  };

  const isBand = variant === "band";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`mini-lead-form-${source}`}
      className={
        isBand
          ? "rounded-2xl p-6 md:p-7 grid gap-4 md:grid-cols-[1fr,1fr,1fr,auto,auto] md:items-end"
          : "space-y-4"
      }
      style={{
        backgroundColor: "#1A1A1A",
        border: "1px solid #262626",
      }}
    >
      {headline && (
        <div className={isBand ? "md:col-span-full" : ""}>
          <h3 className="text-white" style={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-0.01em" }}>
            {headline}
          </h3>
          {subhead && (
            <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
              {subhead}
            </p>
          )}
        </div>
      )}

      <div>
        <label htmlFor={`mlf-${source}-name`} className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
          Name
        </label>
        <input
          id={`mlf-${source}-name`}
          data-testid={`mlf-${source}-name`}
          value={form.name}
          onChange={set("name")}
          required
          style={inputStyle}
          placeholder="Your name"
        />
      </div>

      <div>
        <label htmlFor={`mlf-${source}-company`} className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
          Company name
        </label>
        <input
          id={`mlf-${source}-company`}
          data-testid={`mlf-${source}-company`}
          value={form.company}
          onChange={set("company")}
          required
          style={inputStyle}
          placeholder="Brand or company"
        />
      </div>

      <div>
        <label htmlFor={`mlf-${source}-email`} className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
          Email
        </label>
        <input
          id={`mlf-${source}-email`}
          data-testid={`mlf-${source}-email`}
          type="email"
          value={form.email}
          onChange={set("email")}
          required
          style={inputStyle}
          placeholder="you@brand.com"
        />
      </div>

      <div>
        <span className="block" style={{ fontSize: "13px", color: "#B8B8B8", marginBottom: "6px" }}>
          Live on Quick Commerce
        </span>
        <div className="flex gap-2" data-testid={`mlf-${source}-live`}>
          {["Yes", "No"].map((v) => {
            const active = form.liveOnQc === v;
            return (
              <button
                key={v}
                type="button"
                data-testid={`mlf-${source}-live-${v.toLowerCase()}`}
                onClick={() => handleLive(v)}
                aria-pressed={active}
                style={{
                  height: "44px",
                  minWidth: "64px",
                  padding: "0 18px",
                  borderRadius: "999px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  backgroundColor: active ? "rgba(241,89,43,0.16)" : "#0F0F0F",
                  color: active ? "#FFFFFF" : "#B8B8B8",
                  border: active ? "1px solid #F1592B" : "1px solid #262626",
                  transition: "all 0.15s ease",
                }}
              >
                {v}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="submit"
        data-testid={`mlf-${source}-submit`}
        className="inline-flex items-center justify-center gap-2"
        style={{
          backgroundColor: "#F1592B",
          color: "#FFFFFF",
          height: "44px",
          padding: "0 22px",
          borderRadius: "10px",
          fontSize: "14px",
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <MessageCircle className="h-4 w-4" />
        Send on WhatsApp
      </button>
    </form>
  );
};

export default MiniLeadForm;
